import React, { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Button, Select, TextInput, Checkbox } from 'components/Form'
import { createApiRecord, updateApiRecord } from 'services/XeroIntegrationService'
import { useDispatch, useSelector } from 'react-redux'
import { getRecord } from './store/dataSlice'
import reducer from './store'
import { injectReducer } from 'store'
import { Field, Form, Formik } from 'formik'
import * as Yup from 'yup'
import { Success } from 'utils/alerts'

injectReducer('xeroIntegration', reducer)

const validationSchema = Yup.object().shape({
    company: Yup.string().required('Company field is required'),
    customer: Yup.string().nullable(),
})

const Create = () => {
    const { id } = useParams()
    const dispatch = useDispatch()
    const navigate = useNavigate()

    const { companyData, customersData } = useSelector(state => state.listData)
    const { view, record, loading } = useSelector(state => state.xeroIntegration.data)

    useEffect(() => {
        if (id) {
            fetchData(id)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id])

    const fetchData = id => dispatch(getRecord(id))

    const handleDiscard = () => navigate('/company-management/xero-integration')

    const handleSubmit = data => {
        // Transform form data to match API expectations
        const apiData = {
            ...data,
            company_id: data.company ? parseInt(data.company) : null,
            customer_id: data.customer ? parseInt(data.customer) : null,
        }

        // Remove the original company and customer fields
        delete apiData.company
        delete apiData.customer

        if (id) {
            updateApiRecord(id, apiData)
                .then(() => {
                    Success('Updated Successfully!')
                    handleDiscard()
                })
                .catch(error => console.log('Error: ', error))
        } else {
            createApiRecord(apiData)
                .then(response => {
                    Success('Created Successfully!')
                    handleDiscard()
                })
                .catch(error => console.log('Error: ', error))
        }
    }

    if (loading) {
        return null
    }

    return (
        <>
            <div className="flex flex-col gap-1 mb-3 sm:flex-row sm:items-center sm:justify-between">
                <h2 className="font-semibold text-black text-title-md2 dark:text-white">Xero Integration</h2>
            </div>
            <div className="flex flex-col items-center">
                <div className="w-full bg-white border rounded-sm border-stroke shadow-default dark:border-strokedark dark:bg-boxdark">
                    <div className="px-5 py-3 border-b border-stroke dark:border-strokedark">
                        <h3 className="font-medium text-black dark:text-white">
                            {id ? 'Update Xero Integration' : 'Add New Xero Integration'}
                        </h3>
                    </div>
                    <div className="flex flex-col gap-3 p-6">
                        <Formik
                            initialValues={id ? record : view}
                            validationSchema={validationSchema}
                            onSubmit={values => {
                                handleSubmit(values)
                            }}
                        >
                            {({ values, touched, errors }) => (
                                <Form>
                                    <div className="grid grid-cols-1 gap-x-4 gap-y-4 md:grid-cols-4">
                                        <Field name="company">
                                            {({ field, form }) => (
                                                <Select
                                                    id="company"
                                                    name="company"
                                                    label="Company *"
                                                    placeholder="Select Company"
                                                    className="md:col-span-2"
                                                    value={values?.company}
                                                    options={companyData}
                                                    onChange={e => form.setFieldValue(field.name, e.target.value)}
                                                    error={touched.company && errors.company}
                                                    message={errors.company}
                                                />
                                            )}
                                        </Field>
                                        <Field name="customer">
                                            {({ field, form }) => (
                                                <Select
                                                    id="customer"
                                                    name="customer"
                                                    label="Customer (Optional)"
                                                    placeholder="Select Customer (Leave blank for company-level integration)"
                                                    className="md:col-span-2"
                                                    value={values?.customer}
                                                    options={customersData}
                                                    onChange={e => form.setFieldValue(field.name, e.target.value)}
                                                    error={touched.customer && errors.customer}
                                                    message={errors.customer}
                                                />
                                            )}
                                        </Field>
                                        {/* Client ID and Client Secret fields removed - now using backend environment variables */}
                                        <div className="md:col-span-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-md">
                                            <p className="text-sm text-blue-600 dark:text-blue-400">
                                                <strong>Note:</strong> Xero API credentials are now securely stored in the
                                                backend. You only need to select a company and optional customer for this
                                                integration. After creating the configuration, use the one-click authorization
                                                button on the main page.
                                            </p>
                                        </div>
                                        <Field name="tenant_id">
                                            {({ field, form }) => (
                                                <TextInput
                                                    type="text"
                                                    className="md:col-span-4"
                                                    label="Tenant ID (Auto-populated)"
                                                    name="tenant_id"
                                                    placeholder="This will be automatically populated after OAuth authorization"
                                                    value={values?.tenant_id}
                                                    onChange={e => form.setFieldValue(field.name, e.target.value)}
                                                    readOnly={!id}
                                                    disabled={!id}
                                                />
                                            )}
                                        </Field>
                                        <Field name="is_test_mode">
                                            {({ field, form }) => (
                                                <div className="md:col-span-2">
                                                    <Checkbox
                                                        id="is_test_mode"
                                                        name="is_test_mode"
                                                        checked={values?.is_test_mode || false}
                                                        onChange={e => form.setFieldValue(field.name, e.target.checked)}
                                                        label="Test Mode"
                                                    />
                                                    <p className="text-xs text-gray-500 mt-1">
                                                        Enable test mode for development and testing
                                                    </p>
                                                </div>
                                            )}
                                        </Field>
                                        <Field name="is_active">
                                            {({ field, form }) => (
                                                <div className="md:col-span-2">
                                                    <Checkbox
                                                        id="is_active"
                                                        name="is_active"
                                                        checked={values?.is_active !== false}
                                                        onChange={e => form.setFieldValue(field.name, e.target.checked)}
                                                        label="Active Status"
                                                    />
                                                    <p className="text-xs text-gray-500 mt-1">
                                                        Enable or disable this integration
                                                    </p>
                                                </div>
                                            )}
                                        </Field>
                                    </div>

                                    {/* OAuth Information Panel */}
                                    <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                                        <h4 className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-2">
                                            Xero Integration Setup Instructions
                                        </h4>
                                        <div className="text-xs text-blue-800 dark:text-blue-200 space-y-1">
                                            <p>
                                                <strong>1.</strong> Create a Xero app in the Xero Developer Portal
                                            </p>
                                            <p>
                                                <strong>2.</strong> Copy the Client ID and Client Secret from your Xero app
                                            </p>
                                            <p>
                                                <strong>3.</strong> Select the Company (required) and Customer (optional for
                                                customer-specific integrations)
                                            </p>
                                            <p>
                                                <strong>4.</strong> Save this configuration
                                            </p>
                                            <p>
                                                <strong>5.</strong> Use the "Test Connection" button to authorize with Xero
                                            </p>
                                            <p>
                                                <strong>6.</strong> The Tenant ID will be automatically populated after successful
                                                authorization
                                            </p>
                                            <p className="mt-2 text-blue-700 dark:text-blue-300">
                                                <strong>Note:</strong> Leave Customer blank for company-level integration, or
                                                select a specific customer for customer-specific Xero access.
                                            </p>
                                        </div>
                                    </div>

                                    <div className="flex items-center justify-end gap-4 mt-6">
                                        <Button
                                            className="h-10 w-18 text-primary border border-primary dark:text-secondary dark:border-secondary"
                                            onClick={handleDiscard}
                                        >
                                            Discard
                                        </Button>
                                        <Button
                                            type="submit"
                                            className="flex items-center justify-center px-6 py-2 text-white transition border rounded-lg cursor-pointer hover:bg-opacity-90 border-primary bg-primary dark:border-secondary dark:bg-secondary"
                                        >
                                            Save
                                        </Button>
                                    </div>
                                </Form>
                            )}
                        </Formik>
                    </div>
                </div>
            </div>
        </>
    )
}

export default Create
